import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';

const twoFASchema = z.object({
  code: z.string().length(6, 'Enter the 6-digit code from your authenticator app'),
});

export type ITwoFAForm = z.infer<typeof twoFASchema>;

type Props = {
  onSubmit: (data: ITwoFAForm) => Promise<void>;
};

const TwoFAForm = ({ onSubmit }: Props) => {
  const form = useForm<ITwoFAForm>({
    resolver: zodResolver(twoFASchema),
    defaultValues: {
      code: '',
    },
    mode: 'onChange',
  });

  const handleSubmit = async (data: ITwoFAForm) => {
    try {
      await onSubmit(data);
    } catch (e: unknown) {
      form.setError('code', { type: 'server', message: e instanceof Error ? e.message : 'Something went wrong' });
    }
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
        <FormField
          control={form.control}
          name="code"
          render={({ field, fieldState }) => (
            <FormItem className="flex flex-col items-center gap-2 space-y-0">
              <FormControl>
                <InputOTP
                  maxLength={6}
                  {...field}
                  onChange={(value) => {
                    field.onChange(value);
                    if (value.length === 6 && !form.formState.isSubmitting) {
                      void form.handleSubmit(handleSubmit)();
                    }
                  }}
                >
                  <InputOTPGroup className="mx-auto gap-2">
                    {Array.from({ length: 6 }).map((_, index) => (
                      <InputOTPSlot key={index} index={index} className="border border-input h-14 w-12 rounded-md" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </FormControl>
              {fieldState.error && <FormMessage>{fieldState.error.message}</FormMessage>}
            </FormItem>
          )}
        />
        <Button type="submit" size="lg" className="w-full" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Verifying…' : 'Submit code'}
        </Button>
      </form>
    </Form>
  );
};

export default TwoFAForm;
